// Payment method types supported by the application
export type PaymentMethodType = 'card' | 'cash' | 'cheque' | 'account_credit' | 'us_bank_account' | 'card_reader';

// Stripe card details
export interface CardDetails {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

// Stripe US bank account details
export interface USBankAccountDetails {
  bank_name: string;
  last4: string;
  account_type: 'checking' | 'savings';
  account_holder_type: 'individual' | 'company';
  routing_number?: string;
  financial_connections_account?: string;
}

// Unified saved payment method interface
export interface SavedPaymentMethod {
  id: string;
  type: 'card' | 'us_bank_account';
  card?: CardDetails;
  us_bank_account?: USBankAccountDetails;
  isDefault: boolean;
  created?: number;
}

// Bank account verification status
export type BankAccountVerificationStatus = 'verified' | 'pending' | 'verification_failed';

// Bank account with verification info
export interface BankAccountWithVerification extends SavedPaymentMethod {
  type: 'us_bank_account';
  verificationStatus?: BankAccountVerificationStatus;
  verificationType?: 'instant' | 'microdeposits';
  microdepositsDeadline?: string;
}

// Check/Cheque payment details
export interface CheckDetails {
  checkNumber: string;
  bankName: string;
  checkDate: string;
}

// Payment submission data for modals
export interface PaymentSubmissionData {
  method: PaymentMethodType;
  amount: number;
  useSavedCard?: boolean;
  useSavedBankAccount?: boolean;
  paymentMethodId?: string;
  checkDetails?: CheckDetails;
  note?: string;
  receiptNumber?: string;
  paymentDate?: string;
}

// ACH Setup Intent response
export interface ACHSetupIntentResponse {
  success: boolean;
  clientSecret: string;
  setupIntentId: string;
}

// ACH verification response
export interface ACHVerificationResponse {
  success: boolean;
  status: 'succeeded' | 'requires_action' | 'processing';
  paymentMethodId?: string;
  message?: string;
}

// Payment method display helpers
export const getPaymentMethodDisplayName = (type: PaymentMethodType): string => {
  switch (type) {
    case 'card':
      return 'Card';
    case 'us_bank_account':
      return 'Bank Account';
    case 'cash':
      return 'Cash';
    case 'cheque':
      return 'Check';
    case 'account_credit':
      return 'Account Credit';
    case 'card_reader':
      return 'Card (reader)';
    default:
      return type;
  }
};

// Get display string for a saved payment method
export const formatPaymentMethodDisplay = (method: SavedPaymentMethod): string => {
  if (method.type === 'card' && method.card) {
    return `${method.card.brand.toUpperCase()} •••• ${method.card.last4}`;
  }
  if (method.type === 'us_bank_account' && method.us_bank_account) {
    const accountType = method.us_bank_account.account_type === 'checking' ? 'Checking' : 'Savings';
    return `${method.us_bank_account.bank_name} ${accountType} •••• ${method.us_bank_account.last4}`;
  }
  return 'Unknown payment method';
};

// Get icon name for payment method type
export const getPaymentMethodIcon = (type: 'card' | 'us_bank_account'): string => {
  return type === 'card' ? 'credit-card' : 'building-columns';
};

// Check if payment method is a bank account
export const isBankAccount = (method: SavedPaymentMethod): method is BankAccountWithVerification => {
  return method.type === 'us_bank_account';
};

// Check if payment method is a card
export const isCard = (method: SavedPaymentMethod): boolean => {
  return method.type === 'card';
};

// ACH processing time message
export const ACH_PROCESSING_MESSAGE = 'ACH bank transfers typically take 3-5 business days to process. You will receive a confirmation email once the payment clears.';

// ACH mandate text (required by Stripe)
export const getACHMandateText = (businessName: string): string => {
  return `By providing your bank account information and confirming this payment, you authorize ${businessName} and Stripe, our payment service provider, to debit your bank account for any amounts owed. You also authorize us to credit your bank account to correct any erroneous debits.`;
};
